import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MessageCircleQuestion } from "lucide-react";
import { publicApi } from "../lib/api";

// Which subject in the library a service page belongs to. Hand-mapped: the
// alternative is guessing from the slug and getting payroll answers onto a VAT
// page.
const SUBJECT_BY_SERVICE = [
  [/payroll|pension|auto.?enrol/, "payroll-pensions"],
  [/legal|cashier|conveyanc/, "solicitors-accounts"],
  [/vat|tax|self.?assessment|mtd|corporation|ltd.?co|year.?end/, "tax-vat-mtd"],
  [/management.?account|cash.?flow|forecast|budget/, "management-accounts"],
  [/outsourc|finance.?director|virtual.?finance|growth/, "outsourced-finance"],
  [/bookkeep|xero|software|training|support|troubleshoot|implementation|migrat/, "bookkeeping-xero"],
];

export const subjectForService = (slug = "") =>
  (SUBJECT_BY_SERVICE.find(([re]) => re.test(slug)) || [])[1] || "";

// Answers live in one place — the Ask Cassie library — and every other page
// links to them rather than keeping its own copy. No FAQ schema here on purpose:
// the library page is the canonical version of the answer.
export const AskCassieRelated = ({ serviceUrl = "", category = "", topic = "", heading, limit = 5 }) => {
  const [answers, setAnswers] = useState([]);

  useEffect(() => {
    let live = true;
    publicApi
      .askCassieRelated({ service_url: serviceUrl, category, q: topic, limit })
      .then((d) => live && setAnswers(d.answers || []))
      .catch(() => {});
    return () => { live = false; };
  }, [serviceUrl, category, topic, limit]);

  if (!answers.length) return null;

  return (
    <section className="py-14 md:py-16 bg-slate-50 border-t border-slate-100" data-testid="ask-cassie-related">
      <div className="max-w-4xl mx-auto px-5 sm:px-6">
        <span className="inline-flex items-center gap-2 text-orange font-bold text-xs tracking-widest uppercase">
          <MessageCircleQuestion size={15} /> From our answer library
        </span>
        <h2 className="font-display font-extrabold text-2xl md:text-3xl text-navy mt-2.5">
          {heading || "Questions owners ask us about this"}
        </h2>

        <ul className="mt-7 space-y-3">
          {answers.map((a) => (
            <li key={a.slug} data-testid={`ask-cassie-related-${a.slug}`}>
              <Link
                to={a.url}
                className="group block bg-white rounded-2xl border border-slate-100 hover:border-orange/40 p-5 transition-colors"
              >
                <h3 className="font-display font-bold text-navy text-base md:text-lg group-hover:text-orange transition-colors">
                  {a.question}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed mt-2 line-clamp-3">{a.quick_answer}</p>
                <span className="inline-flex items-center gap-1.5 text-xs font-bold text-orange mt-3">
                  Read the answer <ArrowRight size={13} className="group-hover:translate-x-0.5 transition-transform" />
                </span>
              </Link>
            </li>
          ))}
        </ul>

        <Link
          to="/ask-cassie"
          data-testid="ask-cassie-related-hub"
          className="inline-flex items-center gap-2 mt-7 text-sm font-bold text-navy hover:text-orange transition-colors"
        >
          Browse the whole library <ArrowRight size={15} />
        </Link>
      </div>
    </section>
  );
};

export default AskCassieRelated;
